import {
    DisplayUnit,
    formatInches,
    FRACTION_MAP,
    isDisplayUnitInInches,
    isFiniteNumber,
    roundDisplayValueTo,
} from '@core';
import type { CorridorParameterMargins } from '@data';

export type CorridorBounds = Pick<CorridorParameterMargins, 'innerMax' | 'innerMin'>;

export type CorridorTone = 'system' | 'goal';

export type CorridorExtent = {
    min: number;
    max: number;
};

export const isValidCorridorBounds = (corridor?: CorridorBounds | null): corridor is CorridorBounds => {
    return !!corridor && isFiniteNumber(corridor.innerMin) && isFiniteNumber(corridor.innerMax);
};

export const getCorridorExtent = (corridors: CorridorBounds[]): CorridorExtent | null => {
    if (!corridors.length) return null;

    return {
        min: Math.min(...corridors.map((corridor) => corridor.innerMin)),
        max: Math.max(...corridors.map((corridor) => corridor.innerMax)),
    };
};

export const getOffsetPercent = (value: number, extent: CorridorExtent) => {
    const total = extent.max - extent.min;
    if (total <= 0) return 50;

    const normalized = ((value - extent.min) / total) * 100;
    return Math.min(100, Math.max(0, normalized));
};

export const formatCorridorLabel = (value: number, unit: DisplayUnit) => {
    if (isDisplayUnitInInches(unit)) {
        const { integer, isNegative, quarterFraction } = formatInches(value);

        return {
            integer,
            isNegative,
            fraction: quarterFraction ? FRACTION_MAP[quarterFraction] : null,
        };
    }

    return {
        integer: String(roundDisplayValueTo({ value, displayUnit: unit })),
        fraction: null,
        isNegative: false,
    };
};
